import { useCallback, useState } from "react";

import {
	useLazyGetPendingBySenderQuery,
	useReassignSchedulesMutation,
} from "@/pages/Private/redux/schedule/schedule.api";

import {
	CampaignAction,
	CampaignPending,
} from "@/pages/Private/pages/Account/schema/pendingSchedules";

import type { Sender } from "../components/CampaignOverview/components/Sender";

interface DeactivatedSenderInfo {
	userId: number;
	emailId?: number;
	name: string;
	type: "email" | "linkedin";
	emailAddress?: string;
}

interface UseCampaignSenderRemovalOptions {
	listId: number;
	campaignId: string;
	currentSenders: Sender[];
	newSenders: Sender[];
	onReassignComplete?: () => void | Promise<void>;
}

interface UseCampaignSenderRemovalReturn {
	checkForPendingSchedules: () => Promise<boolean>;
	confirmReassignment: (actions: CampaignAction[]) => Promise<void>;
	cancelReassignment: () => void;
	showPendingDialog: boolean;
	totalCount: number;
	campaigns: CampaignPending[];
	isLoading: boolean;
}

function findDeactivatedSenders(
	currentSenders: Sender[],
	newSenders: Sender[]
): DeactivatedSenderInfo[] {
	const deactivated: DeactivatedSenderInfo[] = [];

	for (const current of currentSenders) {
		if (!current.active) {
			continue;
		}

		const updated = newSenders.find(
			(s) =>
				s.userId === current.userId &&
				s.type === current.type &&
				s.emailAddress === current.emailAddress
		);

		if (updated && !updated.active) {
			deactivated.push({
				userId: current.userId,
				name: current.name,
				type: current.type as "email" | "linkedin",
				emailAddress: current.emailAddress,
				...(current.type === "email" && current.emailId ? { emailId: current.emailId } : {}),
			});
		}
	}

	return deactivated;
}

export function useCampaignSenderRemoval({
	campaignId,
	currentSenders,
	newSenders,
	onReassignComplete,
}: UseCampaignSenderRemovalOptions): UseCampaignSenderRemovalReturn {
	const [showPendingDialog, setShowPendingDialog] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [campaigns, setCampaigns] = useState<CampaignPending[]>([]);
	// Parallel to `campaigns` — senderForCampaign[i] is the sender whose schedules are in campaigns[i]
	const [senderForCampaign, setSenderForCampaign] = useState<DeactivatedSenderInfo[]>([]);

	const [getPendingBySender, { isFetching }] = useLazyGetPendingBySenderQuery();
	const [reassignSchedules, { isLoading: isReassigning }] = useReassignSchedulesMutation();

	const checkForPendingSchedules = useCallback(async (): Promise<boolean> => {
		const removed = findDeactivatedSenders(currentSenders, newSenders);

		if (removed.length === 0) {
			return true;
		}

		const allCampaigns: CampaignPending[] = [];
		const senderMap: DeactivatedSenderInfo[] = [];
		let total = 0;

		for (const sender of removed) {
			const result = await getPendingBySender({
				userId: sender.userId,
				...(sender.emailId ? { emailId: sender.emailId } : {}),
			}).unwrap();

			const filtered = result.campaigns.filter((c) => c.campaignId === campaignId);

			for (const campaign of filtered) {
				// Only show senders that are still active in this campaign
				const activeSendersOnly = campaign.availableSenders.filter((avail) =>
					newSenders.some(
						(s) =>
							s.active &&
							s.userId === avail.userId &&
							s.type === avail.type &&
							s.emailAddress === avail.emailAddress
					)
				);

				allCampaigns.push({
					...campaign,
					availableSenders: activeSendersOnly,
					originSender: {
						name: sender.name,
						type: sender.type,
						emailAddress: sender.emailAddress,
					},
				});
				senderMap.push(sender);
				total += campaign.pendingCount;
			}
		}

		if (total > 0) {
			setCampaigns(allCampaigns);
			setSenderForCampaign(senderMap);
			setTotalCount(total);
			setShowPendingDialog(true);

			return false;
		}

		return true;
	}, [currentSenders, newSenders, campaignId, getPendingBySender]);

	const confirmReassignment = useCallback(
		async (actions: CampaignAction[]) => {
			const enrichedActions = actions
				.map((action, index) => {
					const sender = senderForCampaign[index];

					return {
						...action,
						...(sender ? { oldUserId: sender.userId } : {}),
						...(sender?.emailId ? { oldEmailId: sender.emailId } : {}),
					};
				})
				.filter((a) => a.action !== "skip");

			if (enrichedActions.length > 0) {
				await reassignSchedules({ campaignActions: enrichedActions }).unwrap();
			}

			setShowPendingDialog(false);
			setCampaigns([]);
			setTotalCount(0);
			setSenderForCampaign([]);
			await onReassignComplete?.();
		},
		[senderForCampaign, reassignSchedules, onReassignComplete]
	);

	const cancelReassignment = useCallback(() => {
		setShowPendingDialog(false);
		setCampaigns([]);
		setTotalCount(0);
		setSenderForCampaign([]);
	}, []);

	return {
		checkForPendingSchedules,
		confirmReassignment,
		cancelReassignment,
		showPendingDialog,
		totalCount,
		campaigns,
		isLoading: isFetching || isReassigning,
	};
}
